import React from 'react';

/**
 * UserAvatar — circular avatar that shows the user's photo or coloured initials fallback.
 *
 * Props:
 *   user: { full_name, email, role, photo_url }
 *   size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' (default 'md')
 *   ring: boolean — add a subtle dark ring (for use on light backgrounds in dropdowns)
 *   className: extra classes
 *   onClick: optional click handler
 */

const ROLE_COLOR = {
  admin: 'bg-red-500',
  master_admin: 'bg-red-600',
  cleaner: 'bg-blue-500',
  client: 'bg-emerald-500',
};

const SIZES = {
  xs: { box: 'w-6 h-6', text: 'text-[9px]' },
  sm: { box: 'w-8 h-8', text: 'text-[11px]' },
  md: { box: 'w-10 h-10', text: 'text-sm' },
  lg: { box: 'w-16 h-16', text: 'text-lg' },
  xl: { box: 'w-24 h-24', text: 'text-2xl' },
};

export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const UserAvatar = ({
  user,
  size = 'md',
  ring = false,
  className = '',
  onClick,
  testId = 'user-avatar',
}) => {
  const s = SIZES[size] || SIZES.md;
  const bg = ROLE_COLOR[user?.role] || 'bg-zinc-500';
  const initials = getInitials(user?.full_name || user?.email);
  const ringCls = ring ? 'ring-2 ring-zinc-800' : '';
  const cls = `gg-avatar ${s.box} rounded-full overflow-hidden flex items-center justify-center text-white font-bold shrink-0 ${ringCls} ${className}`;

  if (user?.photo_url) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cls}
        data-testid={testId}
        aria-label={user.full_name || 'User avatar'}
      >
        <img
          src={user.photo_url}
          alt={user.full_name || ''}
          className="w-full h-full object-cover"
          draggable={false}
        />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${cls} ${bg}`}
      data-testid={testId}
      aria-label={user?.full_name || 'User avatar'}
    >
      <span className={s.text}>{initials}</span>
    </button>
  );
};

export default UserAvatar;
